const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

export interface ChatAttachment {
  key: string
  url: string
  name: string
  mimeType: string
  size: number
}

export interface ChatMessage {
  id: string
  orderId: string
  senderId: string
  senderName: string
  senderAvatarUrl?: string
  senderDepartment?: string
  senderCargo?: string
  senderCompanyId: string
  senderCompanyType: 'shipper' | 'freight-forwarder'
  content: string
  attachment: ChatAttachment | null
  readByShipper: boolean
  readByFf: boolean
  createdAt: string
  isOwnMessage?: boolean
  deletedAt?: string | null
}

export interface GetMessagesResponse {
  messages: ChatMessage[]
  total: number
  limit: number
  offset: number
}

export interface SendMessageResponse {
  message: ChatMessage
}

export interface UnreadCountResponse {
  unreadCount: number
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = localStorage.getItem('access_token')
  return {
    'Authorization': `Bearer ${token}`
  }
}

export const chatService = {
  /**
   * Get chat messages for an order with pagination
   */
  async getMessages(orderId: string, limit = 50, offset = 0): Promise<GetMessagesResponse> {
    const headers = await getAuthHeaders()
    const response = await fetch(
      `${API_URL}/orders/${orderId}/messages?limit=${limit}&offset=${offset}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro ao buscar mensagens')
    }

    return response.json()
  },

  /**
   * Send a text message
   */
  async sendMessage(orderId: string, content: string): Promise<SendMessageResponse> {
    const headers = await getAuthHeaders()
    const response = await fetch(
      `${API_URL}/orders/${orderId}/messages`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        },
        body: JSON.stringify({ content })
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro ao enviar mensagem')
    }

    return response.json()
  },

  /**
   * Send a file attachment with optional caption
   */
  async sendAttachment(
    orderId: string,
    file: File,
    caption?: string
  ): Promise<SendMessageResponse> {
    const headers = await getAuthHeaders()

    const formData = new FormData()
    formData.append('file', file)
    if (caption) {
      formData.append('caption', caption)
    }

    const response = await fetch(
      `${API_URL}/orders/${orderId}/messages/attachment`,
      {
        method: 'POST',
        headers, // Don't set Content-Type for FormData
        body: formData
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro ao enviar anexo')
    }

    return response.json()
  },

  /**
   * Mark all messages as read for the current user's company
   */
  async markAsRead(orderId: string): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders()
    const response = await fetch(
      `${API_URL}/orders/${orderId}/messages/read`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro ao marcar como lido')
    }

    return response.json()
  },

  /**
   * Get unread message count for an order
   */
  async getUnreadCount(orderId: string): Promise<UnreadCountResponse> {
    const headers = await getAuthHeaders()
    const response = await fetch(
      `${API_URL}/orders/${orderId}/messages/unread-count`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro ao buscar contagem')
    }

    return response.json()
  },

  /**
   * Delete a message (only sender can delete their own messages)
   */
  async deleteMessage(orderId: string, messageId: string): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders()
    const response = await fetch(
      `${API_URL}/orders/${orderId}/messages/${messageId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro ao apagar mensagem')
    }

    return response.json()
  }
}

export default chatService
